/**
 * `DetailSurfaceBackHeader` — proto-parity sub-header for every
 * article-detail surface.
 *
 * A fixed bar that sits directly below the shell's `TopBar`, exposes a
 * single icon-only round button, and pops the SPA history when the user
 * taps it. Mirrors the proto's article pages (`TyosuhteenPaattaminen`,
 * `Tyoelamaopas`, `Settings`, `MessageDetail`, `Recommend`,
 * `Jasenedut`, `ContentDetail`), each of which wears the same
 * back-arrow chrome.
 *
 * Token discipline
 * ================
 *
 * The proto uses `top-[var(--nav-height)]` to position the bar below
 * its own TopBar; we deliberately diverge to `top-top-bar` (token-
 * backed via `--spacing-top-bar`) because FR-047 / the
 * `no-token-bypass` arch test forbids `*-[…]` arbitrary classes in
 * application code. Likewise the bar's height comes from the new
 * `--spacing-back-header` token (`h-back-header`), not from a literal.
 *
 * Back behaviour
 * ==============
 *
 * A fixed target takes precedence. Otherwise the shared router boundary
 * permits a history pop only to a known member entry in this document.
 * Direct entry, replacement without a known predecessor, and login/auth predecessors use the
 * surface's feature home. Browser history length includes external sites
 * and cannot establish whether it is safe to pop.
 *
 * Focus discipline
 * ================
 *
 * The button carries the same `focus-visible:outline-none
 * focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-ring`
 * treatment as `TopBar`, `PrimaryNav`, and `MenuSheet`. Keyboard users
 * see a consistent ring across every shell control rather than a
 * surface-specific divergence.
 *
 * A11y landmark
 * =============
 *
 * The wrapper is a `<nav>` with `aria-label={label}`
 * so AT users navigate to the back-header via landmark navigation and
 * hear "Takaisin / Back". The same Finnish copy is used for both the
 * landmark name and the button's `aria-label`; AT pronounces them at
 * different DOM levels and they do not collide. The icon itself is
 * `aria-hidden` via `baseSvgProps` so AT does not double-announce
 * alongside the inline `<title>Back</title>`.
 *
 * Why not place the bar inside `pageClasses`
 * ==========================================
 *
 * `pageClasses` is the per-route `<article>`/`<section>` wrapper.
 * Placing the back-header inside that wrapper would put a fixed-
 * positioned chrome bar inside the page's main landmark, conflating
 * navigation chrome with content. The bar is rendered as the
 * preceding sibling of the page wrapper instead, with `pt-back-header`
 * on `pageClasses` reserving the matching offset (CLS-safe; the bar
 * never overlaps the article body). See `detailSurfacePageClasses.ts`
 * for the constant.
 *
 * Padding stack
 * =============
 *
 * The article body's distance from the viewport top is the sum of
 * three contributing tokens, applied at three different layout
 * levels:
 *
 * Layout reserves the fixed header's height before the sitewide banner and
 * consent notice. The surface's standalone `pt-back-header` reservation is
 * suppressed inside that layout, so it is not counted twice. This keeps
 * sitewide notices below the opaque navigation band.
 *
 * Testability
 * ===========
 *
 * The wrapper carries `data-testid="article-back-header"` and the
 * button carries `data-testid="article-back-header-button"` for
 * route-level integration tests that need to assert "this surface
 * renders the back-header" without re-asserting the markup shape
 * (the colocated `__tests__/detailSurfaceBackHeader.test.tsx` pins
 * the shape contract once).
 */

import type { JSX } from 'react';
import { useNavigate } from 'react-router';

import { useTranslate } from '../../i18n/useTranslate.js';
import { BackArrowIcon } from '../../shell/icons.js';
import { useMemberBackHistory } from '../../shell/MemberBackHistory.js';

// At desktop width (058) the band starts beside the sidebar and its inner
// width follows the content column. The vertical position does not change:
// the top bar is not mounted there, and `top-top-bar` is the prototype's
// `--nav-height` offset either way.
const wrapperClasses: string =
  'fixed top-top-bar left-0 lg:left-desktop-sidebar right-0 z-30 h-back-header bg-background ' +
  'border-b border-border';
const innerClasses: string =
  'mx-auto max-w-md lg:max-w-content-column h-full px-4 flex items-center';
const buttonClasses: string =
  'rounded-full h-11 w-11 bg-muted hover:bg-muted active:bg-muted active:scale-95 ' +
  'flex items-center justify-center ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-ring';
const iconClasses: string = 'h-5 w-5';

export interface DetailSurfaceBackHeaderProps {
  readonly fallbackTo?: string | undefined;
  readonly fixedTo?: string | undefined;
  readonly backLabel?: string | undefined;
}

export function DetailSurfaceBackHeader({
  fallbackTo = '/',
  fixedTo,
  backLabel,
}: DetailSurfaceBackHeaderProps = {}): JSX.Element {
  const t = useTranslate();
  const navigate = useNavigate();

  const knownHistory = useMemberBackHistory();
  const label = backLabel ?? t('article.backHeader.label');

  function handleBack(): void {
    if (fixedTo !== undefined) {
      navigate(fixedTo);
    } else if (knownHistory === true) {
      navigate(-1);
    } else {
      navigate(fallbackTo, { replace: true });
    }
  }

  return (
    <nav
      aria-label={label}
      className={wrapperClasses}
      data-testid="article-back-header"
      data-member-back-header
    >
      <div className={innerClasses}>
        <button
          type="button"
          onClick={handleBack}
          aria-label={label}
          data-testid="article-back-header-button"
          className={buttonClasses}
        >
          <BackArrowIcon className={iconClasses} />
        </button>
      </div>
    </nav>
  );
}
